/**
 * Audio format utilities for cross-browser compatibility
 * Uses music-metadata for robust metadata extraction
 */

import { IAudioMetadata, parseBlob } from 'music-metadata';

/**
 * Get list of supported MIME types in preference order
 * Using MP4 universally for maximum browser compatibility (including iOS)
 */
export const SUPPORTED_MIME_TYPES = [
  'audio/mp4', // Universal support, including iOS
  'audio/wav', // Fallback
] as const;

/**
 * Audio metadata extracted from file
 */
export interface AudioMetadata {
  /** Bitrate in bits per second */
  bitrate?: number;
  /** Number of channels */
  channels?: number;
  /** Codec (e.g., 'mp3', 'aac') */
  codec?: string;
  /** Duration in milliseconds */
  duration: number;
  /** File format (e.g., 'MP3', 'MP4', 'WAV') */
  format?: string;
  /** Sample rate in Hz */
  sampleRate?: number;
}

/**
 * Result of audio metadata extraction
 */
export interface AudioMetadataResult {
  /** Original or processed blob */
  blob: Blob;
  /** Extracted metadata */
  metadata: AudioMetadata;
}

/**
 * Extract audio metadata from a blob using music-metadata
 * Single Responsibility: Only extracts metadata, doesn't transform
 * @param blob - Audio blob to analyze
 * @returns Promise with metadata
 * @throws Error if blob is not a valid audio file
 */
export async function extractAudioMetadata(blob: Blob): Promise<AudioMetadata> {
  try {
    const metadata: IAudioMetadata = await parseBlob(blob);

    return {
      codec: metadata.format.codec,
      bitrate: metadata.format.bitrate,
      sampleRate: metadata.format.sampleRate,
      channels: metadata.format.numberOfChannels,
      duration: (metadata.format.duration || 0) * 1000, // Convert to milliseconds
      format: metadata.format.container?.toUpperCase(),
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to extract audio metadata: ${errorMessage}`);
  }
}

/**
 * Get audio metadata with the original blob
 * Open/Closed: Extensible through AudioMetadata interface
 * @param blob - Audio blob to process
 * @returns Promise with blob and metadata
 */
export async function getAudioMetadata(blob: Blob): Promise<AudioMetadataResult> {
  const metadata = await extractAudioMetadata(blob);

  return {
    blob,
    metadata,
  };
}

/**
 * Get the preferred MIME type for audio recording
 * Returns MP4 for universal browser compatibility
 * @returns MIME type string or empty string if none supported
 */
export function getPreferredMimeType(): string {
  if (typeof MediaRecorder === 'undefined') {
    return '';
  }

  // Prefer MP4 for universal compatibility (especially iOS)
  for (const type of SUPPORTED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  return '';
}

/**
 * Validate if a blob is a supported audio format
 * @param blob - Blob to validate
 * @returns true if blob appears to be audio
 */
export function isAudioBlob(blob: Blob): boolean {
  return (
    blob.type.startsWith('audio/') ||
    blob.type === 'video/webm' || // WebM from MediaRecorder can be video/webm even for audio-only
    blob.type === ''
  );
}
